import { memo, useEffect, useMemo } from "react";
import { MapContainer, Marker, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import { AlertCircle } from "lucide-react";
import type { Shop } from "@shared/schema";
import { getIconForShop } from "@/lib/mapIcons";
import { DEFAULT_CENTER, DEFAULT_ZOOM } from "@/lib/googleMaps";
import { OSM_TILE_ATTRIBUTION, OSM_TILE_URL } from "@/lib/osmMaps";
import { useI18n } from "@/lib/i18n";
import { MapInteractionShield } from "./MapInteractionShield";
import { useRoutingConfig } from "@/hooks/use-routing-config";

interface ShopsSearchMapProps {
  shops: Shop[];
  selectedShopId?: string | null;
  onShopSelect?: (shopId: string) => void;
  height?: string;
  className?: string;
  center?: { lat: number; lng: number } | null;
  zoom?: number | null;
  bounds?: {
    northeast: { lat: number; lng: number };
    southwest: { lat: number; lng: number };
  } | null;
}

type ShopWithCoords = Shop & { lat: number; lng: number };

function validateCoordinates(lat: number | null | undefined, lng: number | null | undefined): boolean {
  if (lat == null || lng == null) return false;
  if (typeof lat !== "number" || typeof lng !== "number") return false;
  if (Number.isNaN(lat) || Number.isNaN(lng)) return false;
  return true;
}

function makeShopIcon(shop: Shop, selected: boolean): L.Icon {
  const icon = getIconForShop({ ...shop, isFavorite: selected });
  const baseWidth = icon.scaledSize?.width || 96;
  const baseHeight = icon.scaledSize?.height || 96;
  const scale = selected ? 1.2 : 1;
  const width = Math.round(baseWidth * scale);
  const height = Math.round(baseHeight * scale);
  const anchorX = icon.anchor?.x ? Math.round(icon.anchor.x * scale) : Math.round(width / 2);
  const anchorY = icon.anchor?.y ? Math.round(icon.anchor.y * scale) : height;

  return L.icon({
    iconUrl: icon.url,
    iconSize: [width, height],
    iconAnchor: [anchorX, anchorY],
    className: selected ? "drop-shadow-[0_0_8px_rgba(244,63,134,0.8)]" : "",
  });
}

function MapViewportController({
  validShops,
  centerOverride,
  zoomOverride,
  explicitBoundsOverride,
}: {
  validShops: ShopWithCoords[];
  centerOverride?: { lat: number; lng: number } | null;
  zoomOverride?: number | null;
  explicitBoundsOverride?: {
    northeast: { lat: number; lng: number };
    southwest: { lat: number; lng: number };
  } | null;
}) {
  const map = useMap();

  useEffect(() => {
    if (explicitBoundsOverride) {
      const bounds = L.latLngBounds(
        [explicitBoundsOverride.southwest.lat, explicitBoundsOverride.southwest.lng],
        [explicitBoundsOverride.northeast.lat, explicitBoundsOverride.northeast.lng]
      );
      const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
      map.fitBounds(bounds, { padding: [isMobile ? 60 : 50, isMobile ? 60 : 50] });
      const maxZoom = isMobile ? 14 : 16;
      if (map.getZoom() > maxZoom) map.setZoom(maxZoom);
      return;
    }

    if (centerOverride && zoomOverride) {
      map.setView([centerOverride.lat, centerOverride.lng], zoomOverride);
      return;
    }

    if (validShops.length > 0) {
      const bounds = L.latLngBounds(validShops.map((s) => [s.lat, s.lng] as [number, number]));
      const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
      map.fitBounds(bounds, { padding: [isMobile ? 60 : 50, isMobile ? 60 : 50] });
      const maxZoom = isMobile ? 13 : 15;
      if (map.getZoom() > maxZoom) map.setZoom(maxZoom);
      return;
    }

    if (centerOverride) {
      map.setView([centerOverride.lat, centerOverride.lng], zoomOverride || DEFAULT_ZOOM);
    }
  }, [map, validShops, centerOverride, zoomOverride, explicitBoundsOverride]);

  return null;
}

function ShopsSearchMapOsmBase({
  shops,
  selectedShopId,
  onShopSelect,
  height = "100%",
  className = "",
  center: centerOverride,
  zoom: zoomOverride,
  bounds: explicitBoundsOverride,
}: ShopsSearchMapProps) {
  const { t } = useI18n();
  const effectiveHeight = height || "500px";

  const validShops = useMemo<ShopWithCoords[]>(
    () => shops.filter((s): s is ShopWithCoords => validateCoordinates(s.lat, s.lng)),
    [shops]
  );

  const invalidShops = useMemo(
    () =>
      import.meta.env.DEV
        ? shops
          .filter((s) => !validateCoordinates(s.lat, s.lng))
          .map((s) => ({ name: String(s.name || "Winkel"), reason: "Geen geldige coördinaten" }))
        : [],
    [shops]
  );

  const fallbackCenter = useMemo<[number, number]>(() => {
    if (centerOverride) return [centerOverride.lat, centerOverride.lng];
    if (validShops.length === 0) return [DEFAULT_CENTER.lat, DEFAULT_CENTER.lng];
    const avgLat = validShops.reduce((sum, s) => sum + s.lat, 0) / validShops.length;
    const avgLng = validShops.reduce((sum, s) => sum + s.lng, 0) / validShops.length;
    return [avgLat, avgLng];
  }, [centerOverride, validShops]);

  // Use standard OSM tiles for single shop map
  const tileUrl = OSM_TILE_URL;


  return (
    <div className="flex flex-col gap-2 w-full h-full">
      <div className="relative w-full flex-grow min-h-[300px]" style={effectiveHeight === "100%" ? undefined : { height: effectiveHeight }}>
        {import.meta.env.DEV && invalidShops.length > 0 && (
          <div className="absolute top-2 left-2 z-[1000] bg-yellow-100 border border-yellow-400 rounded p-2 text-xs max-w-xs">
            <div className="flex items-center gap-1 mb-1">
              <AlertCircle className="h-4 w-4 text-yellow-600" />
              <span className="font-semibold text-yellow-800">{invalidShops.length} shop(s) zonder geldige coördinaten</span>
            </div>
          </div>
        )}
        <MapInteractionShield>
          <MapContainer
            center={fallbackCenter}
            zoom={zoomOverride || DEFAULT_ZOOM}
            className={`rounded-lg ${className}`}
            style={{ width: "100%", height: "100%" }}
            zoomControl
          >
            <TileLayer url={tileUrl} attribution={OSM_TILE_ATTRIBUTION} />

            <MapViewportController
              validShops={validShops}
              centerOverride={centerOverride}
              zoomOverride={zoomOverride}
              explicitBoundsOverride={explicitBoundsOverride}
            />

            {validShops.map((shop) => (
              <Marker
                key={shop.id}
                position={[shop.lat, shop.lng]}
                icon={makeShopIcon(shop, selectedShopId === shop.id)}
                eventHandlers={{
                  click: () => onShopSelect?.(shop.id),
                }}
              />
            ))}
          </MapContainer>
        </MapInteractionShield>

      </div>

      {/* Map Legend (Moved below the map) */}
      <div className="flex items-center gap-3 px-1 py-1">
        <div className="w-10 h-10 flex items-center justify-center shrink-0">
          <img src="/markers/Kringloop.svg" alt={t("shops.title")} className="w-full h-full object-contain drop-shadow-sm" />
        </div>
        <span className="text-sm font-semibold text-foreground">{t("shops.title")}</span>
      </div>
    </div>
  );
}

export const ShopsSearchMapOsm = memo(ShopsSearchMapOsmBase);
